import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import qs from 'query-string';

import ym from 'react-yandex-metrika';

import { useStaticUrls, useGeneratePath, getExperimentFlags } from 'platform-components';
import { getExactPathMatch } from 'platform-components/utils';
import { getIsAuthenticated, getMe } from 'platform-components/src/components/user/selectors';
import { getUserName } from 'utils/get-user-name';

import { NOTIFICATION_TYPE } from '@yandex-int/k-common/typings';
import { ContainerElement } from '@yandex-lego/components/Button';

import { getDisplayName, getFirstName, getLastName } from 'platform-components/src/components/user/selectors';

import { IUser } from '@edu-frontend/modules/src/entities/user';
import * as courseSelectors from 'store/courses/selectors';
import { useTeacherReserveParticipant } from 'store/teacher-reserve';
import { WrappedBaseHeader } from '@edu-frontend/modules/src/widgets/wrappedBaseHeader';
import { ClassesSelect } from 'common.components/classes-select/classes-select';
import { getFeatureFlags } from 'platform-components/src/components/app-factory/selectors';
import { getIsInStudentStatsBundle } from 'utils/get-is-in-student-stats-bundle';
import { getClasses } from 'common.components/user/selectors';

import { getHeaderNotifications } from 'platform-components/src/components/notifications/selectors';
import * as salesCodeModalActions from 'common.components/sales-code-modal/actions';
import * as notificationsActions from 'platform-components/src/components/notifications/actions';

import { type HeaderMixedNavItem, isHeaderNavigationSubmenu } from '@edu-frontend/modules/src/features/baseHeader';
import { getTeacherDrilledQueryParams } from 'utils/getTeacherDrilledQueryParams';
import { getLinksParams } from './lib/links';
import { getNotificationIds } from './lib/notifications';
import { useNewLabPathAccess } from './lib/useNewLabPathAccess';
import { useDoodle } from './doodle/Doodle';

import './TeacherHeader.scss';
import { RedirectToNewLab } from './lib/RedirectToNewLab';

interface HeaderProps {
    className?: string;
    hideRightControls?: boolean;
    isMenuDisabled?: boolean;
}

const TABS_WITH_CLASS_SELECT = ['journal', 'subjects', 'mylessons', 'speccourse'];

export function TeacherHeader(props: HeaderProps) {
    const { search } = useLocation();

    return qs.parse(search).lms === 'mes' ? null : <TeacherHeaderInner {...props} />;
}

// eslint-disable-next-line complexity
function TeacherHeaderInner({ className, hideRightControls, isMenuDisabled }: HeaderProps) {
    const intl = useIntl();
    const { formatMessage } = intl;
    const i18n = (id: string) => formatMessage({ id });

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const getRouterPath = useGeneratePath();

    const staticUrls = useStaticUrls();

    const currentUser = useSelector(getMe);
    const isAuthenticatedUser = useSelector(getIsAuthenticated);
    const featureFlags = useSelector(getFeatureFlags);
    const notifications = useSelector(getHeaderNotifications);
    const displayName = useSelector(getDisplayName);
    const firstName = useSelector(getFirstName);
    const lastName = useSelector(getLastName);
    const isInformaticsTeacher = useSelector(courseSelectors.getIsInformaticsTeacher);

    const { pathname, search } = location;
    const match = getExactPathMatch(staticUrls, pathname);

    const { params = {} } = match || {};
    const { classId, subject: subjectFromParams } = params;
    const subject = useSelector((store) =>
        courseSelectors.getSelectedSubjectOrDefault(store, { classId, subject: subjectFromParams })
    );
    const specCourseId = useSelector((store) => courseSelectors.getSpecCourseId(store, { classId }));

    const [viewedNotifications, addNotificationToViewed] = useState(new Set<number>());

    const { handleDoodleClick, serviceLogo, serviceLogoShort, serviceLogoText } = useDoodle();

    const notificationIds = useMemo(() => getNotificationIds(notifications), [notifications]);
    const canUseRouterRedirect = !getIsInStudentStatsBundle(pathname);
    const isContentManager = useMemo(() => (currentUser || {}).is_content_manager, [currentUser]);

    const teams = useSelector(getClasses);
    const { data: participant, isSuccess: participantLoaded } = useTeacherReserveParticipant(!teams.length);

    const sendMetrics = useCallback((metricSlug: string) => {
        ym('reachGoal', 'teacher_click_header', { item: metricSlug });
    }, []);

    const onLinkPress = useCallback(
        ({ notificationId, metricSlug }: { notificationId?: number; metricSlug?: string }) => {
            if (metricSlug) {
                sendMetrics(metricSlug);
            }

            if (notificationId) {
                addNotificationToViewed(viewedNotifications.add(notificationId));

                dispatch(
                    notificationsActions.updateNotificationsRequest({
                        ids: [notificationId],
                        isViewed: true,
                        includeTypes: [NOTIFICATION_TYPE.INTERFACE_ELEMENT_UNREAD],
                    })
                );
            }
        },
        [dispatch, sendMetrics, viewedNotifications]
    );

    const experimentsFlags = useSelector(getExperimentFlags);
    const newLab = useNewLabPathAccess({
        featureFlags,
        experimentsFlags,
        teamId: classId,
        subject,
        isInformaticsTeacher,
        staticUrls,
        userId: currentUser?.id,
    });

    const navigationLinks: HeaderMixedNavItem[] = isSSR
        ? []
        : getLinksParams({
              classId: Number(classId),
              featureFlags,
              i18n,
              isContentManager,
              isInformaticsTeacher,
              isMenuDisabled,
              location,
              notificationIds,
              viewedNotifications,
              onLinkPress,
              specCourseId,
              staticUrls,
              subject,
              getRouterPath,
              isAuthenticatedUser,
              newLab,
          });

    const activeTab = navigationLinks.find((link) => !isHeaderNavigationSubmenu(link) && link.active);

    const onServiceLogoClick = useCallback(
        (e: React.MouseEvent<ContainerElement, MouseEvent>) => {
            e.preventDefault();

            handleDoodleClick();

            ym('reachGoal', 'teacher_click_header', { item: 'schoolbook-logo' });

            const url = getRouterPath(classId ? staticUrls.labLibraryTeamMain : staticUrls.labLibraryMain);
            const drilledParams = getTeacherDrilledQueryParams(search, url);
            const urlWithQueryParams = `${url}?${drilledParams.toString()}`;

            if (canUseRouterRedirect) {
                history.push(urlWithQueryParams);
            } else {
                window.location.href = urlWithQueryParams;
            }
        },
        [
            handleDoodleClick,
            getRouterPath,
            classId,
            staticUrls.labLibraryTeamMain,
            staticUrls.labLibraryMain,
            search,
            canUseRouterRedirect,
            history,
        ]
    );

    const onYandexLogoClick = useCallback(() => {
        ym('reachGoal', 'teacher_click_header', { item: 'yandex-logo' });
    }, []);

    const headerControls = useMemo(() => {
        if (hideRightControls || !isAuthenticatedUser) {
            return null;
        }

        return !activeTab || TABS_WITH_CLASS_SELECT.includes(activeTab.id) ? (
            <ClassesSelect mix="classes-controls" />
        ) : null;
    }, [activeTab, hideRightControls, isAuthenticatedUser]);

    const generatePath = useGeneratePath();
    const generateNewLabPath = useCallback(
        (path: string) => generatePath(path, { teamId: classId, subject }),
        [classId, generatePath, subject]
    );

    const onUserMenuItemClick = useCallback(
        (item: string, _: unknown, { onClose }: { onClose: () => void }) => {
            if (item === 'seminar_code') {
                onClose();
                dispatch(salesCodeModalActions.toggleSalesCodeModal(true));
            }
            if (item === 'personal_account') {
                onClose();
                RedirectToNewLab(generateNewLabPath(newLab.newLabPath.profile));
            }
        },
        [dispatch, generateNewLabPath, newLab.newLabPath.profile]
    );

    const teacherReserveUrl = getRouterPath(classId ? staticUrls.labClassTeacherReserve : staticUrls.labTeacherReserve);

    const navigate = canUseRouterRedirect
        ? (href: string) => {
              if (href.includes(staticUrls.cmHome)) {
                  window.open(href, '_self');
                  return;
              }

              history.push(href);
          }
        : undefined;

    const shouldHaveCounter = !featureFlags?.teacherReserveInactive && isInformaticsTeacher && participantLoaded;

    const onCounterClick =
        navigate &&
        ((e: React.MouseEvent<HTMLAnchorElement>) => {
            const {
                currentTarget: { pathname, search, hash }, // eslint-disable-line @typescript-eslint/no-shadow
            } = e;
            navigate(pathname + search + hash);
            e.preventDefault();
        });

    const userData = useMemo(() => {
        return isAuthenticatedUser ? undefined : ({ status: 'failed', result: null } as const);
    }, [isAuthenticatedUser]);

    const counterPoints =
        ((participant?.level || 0) === 1 ? participant?.pointsAfterLvlUp : participant?.commonPoints) || 0;

    return (
        <WrappedBaseHeader
            className={cx(className, 'TeacherHeader')}
            controls={headerControls}
            counter={(shouldHaveCounter && counterPoints) || undefined}
            counterLink={shouldHaveCounter ? teacherReserveUrl : undefined}
            currentUser={currentUser as IUser}
            loginSettings={{ onLoginButtonClick: () => ym('reachGoal', 'header_entrance') }}
            navigate={navigate}
            navigation={navigationLinks}
            onCounterClick={shouldHaveCounter ? onCounterClick : undefined}
            onServiceLogoClick={onServiceLogoClick}
            onYandexLogoClick={onYandexLogoClick}
            serviceLogo={serviceLogo}
            serviceLogoShort={serviceLogoShort}
            serviceLogoText={serviceLogoText}
            theme="light"
            translations={{
                Войти: i18n('user.enter'),
                Еще: i18n('common.header.more'),
            }}
            userData={userData}
            userName={getUserName(firstName, lastName, displayName)}
            userSettings={{
                avatarId: currentUser?.avatar || undefined,
                // EDUCATION-40029
                fetchUserData: false,
                queryParams: {
                    showCode: currentUser?.is_teacher ? '1' : '',
                    personalAccount: newLab.newLabAccess.profile && currentUser?.is_teacher ? '1' : '',
                },
                onMenuItemClick: onUserMenuItemClick,
            }}
        />
    );
}
